import React, { useState, useCallback, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const MAX_IMAGES = 4;
const API_BASE_URL = process.env.REACT_APP_BACKEND_API;
const MAX_FILE_SIZE_MB = 2; // Max size per image in MB
const MAX_IMAGE_DIMENSION = 1920; // Max width/height in pixels

const ProductForm = ({ onClose, onProductAdded }) => {
  // --- Static Data ---
  const categories = ['Shop Category', 'Indoor', 'Outdoor', 'Wedding', 'Gym', 'Office', 'Meeting', 'School', 'College'];
  const perfumeVolumes = ['30ml', '50ml', '100ml'];
  const attarVolumes = ['3ml', '6ml', '12ml'];
  const stockOptions = ['In stock', 'Out of stock'];
  const productTypes = ['Normal', 'New Arrival'];
  const offerTypes = ['None', 'Discount', 'Offer'];
  const fragranceTypes = ['Perfumes', 'Attars'];

  // --- State ---
  const [formData, setFormData] = useState({
    name: '',
    prices: { '30ml': '', '50ml': '', '100ml': '' },
    discountedPrices: { '30ml': '', '50ml': '', '100ml': '' },
    category: '',
    fragranceType: '',
    fragranceNotes: [],
    currentNote: '',
    stockAvailability: 'In stock',
    description: '',
    productType: 'Normal',
    offerType: 'None'
  });

  const [offerPercentage, setOfferPercentage] = useState('');
  const [newImages, setNewImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  // --- Derived State ---
  const totalImageCount = newImages.length;
  const canUploadMore = totalImageCount < MAX_IMAGES;
  const showDiscountFields = formData.offerType === 'Discount' || formData.offerType === 'Offer';
  const showOfferPercentage = formData.offerType === 'Offer';
  const volumes = formData.fragranceType === 'Attars' ? attarVolumes : perfumeVolumes;

  // --- Image Compression Function ---
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Resize if image is too large
          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_DIMENSION;
              width = MAX_IMAGE_DIMENSION;
            } else {
              width = (width / height) * MAX_IMAGE_DIMENSION;
              height = MAX_IMAGE_DIMENSION;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });

                const sizeMB = compressedFile.size / (1024 * 1024);
                console.log(`Compressed ${file.name}: ${sizeMB.toFixed(2)}MB`);
                
                resolve(compressedFile);
              } else {
                reject(new Error('Canvas to Blob conversion failed'));
              }
            },
            'image/jpeg',
            0.85 // Compression quality (0.85 = 85%)
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  // --- Reset prices when fragrance type changes ---
  useEffect(() => {
    if (formData.fragranceType) {
      const newPrices = {};
      const newDiscountedPrices = {};
      
      volumes.forEach(volume => {
        newPrices[volume] = '';
        newDiscountedPrices[volume] = '';
      });
      
      setFormData(prev => ({
        ...prev,
        prices: newPrices,
        discountedPrices: newDiscountedPrices
      }));
      setOfferPercentage('');
    }
  }, [formData.fragranceType]);

  // --- Auto-calculate offer prices when percentage changes ---
  useEffect(() => {
    if (formData.offerType === 'Offer' && offerPercentage && offerPercentage > 0) {
      const newDiscountedPrices = {};
      volumes.forEach(volume => {
        const originalPrice = parseFloat(formData.prices[volume]);
        if (originalPrice && !isNaN(originalPrice)) {
          const discountAmount = (originalPrice * parseFloat(offerPercentage)) / 100;
          const discountedPrice = originalPrice - discountAmount;
          newDiscountedPrices[volume] = discountedPrice.toFixed(2);
        } else {
          newDiscountedPrices[volume] = '';
        }
      });
      
      setFormData(prev => ({
        ...prev,
        discountedPrices: newDiscountedPrices
      }));
    }
  }, [offerPercentage, formData.prices, formData.offerType, volumes]);

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    if (name === 'offerType' && value !== 'Offer') {
      setOfferPercentage('');
    }
  };

  const handleOfferPercentageChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
      setOfferPercentage(value);
    }
  };

  const handlePriceChange = (volume, value) => {
    setFormData((prev) => ({
      ...prev,
      prices: { ...prev.prices, [volume]: value }
    }));
  };

  const handleDiscountedPriceChange = (volume, value) => {
    setFormData((prev) => ({
      ...prev,
      discountedPrices: { ...prev.discountedPrices, [volume]: value }
    }));
  };

  const handleAddNote = () => {
    if (formData.currentNote.trim()) {
      setFormData((prev) => ({
        ...prev,
        fragranceNotes: [...prev.fragranceNotes, formData.currentNote.trim()],
        currentNote: ''
      }));
    }
  };

  const handleRemoveNote = (index) => {
    setFormData((prev) => ({
      ...prev,
      fragranceNotes: prev.fragranceNotes.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const availableSlots = MAX_IMAGES - totalImageCount;

    if (files.length > availableSlots) {
      toast.error(`You can only upload ${availableSlots} more image(s). Max ${MAX_IMAGES} total.`, {
        position: "top-center", 
        autoClose: 3000,
      });
      return;
    }

    setIsCompressing(true);
    
    try {
      const compressedFiles = await Promise.all(
        files.map(async (file) => {
          // Check if file is an image
          if (!file.type.startsWith('image/')) {
            toast.error(`${file.name} is not an image file`, { position: "top-center" });
            return null;
          }

          // Check original file size
          const originalSizeMB = file.size / (1024 * 1024);
          console.log(`Original ${file.name}: ${originalSizeMB.toFixed(2)}MB`);

          // Compress the image
          try {
            const compressed = await compressImage(file);
            const compressedSizeMB = compressed.size / (1024 * 1024);

            if (compressedSizeMB > MAX_FILE_SIZE_MB) {
              toast.warning(`${file.name} is still too large after compression (${compressedSizeMB.toFixed(2)}MB). Try a smaller image.`, {
                position: "top-center",
                autoClose: 4000
              });
              return null;
            }

            return compressed;
          } catch (error) {
            console.error(`Error compressing ${file.name}:`, error);
            toast.error(`Failed to compress ${file.name}`, { position: "top-center" });
            return null;
          }
        })
      );

      const validFiles = compressedFiles.filter(file => file !== null);
      
      if (validFiles.length > 0) {
        setNewImages((prev) => [...prev, ...validFiles]);
        toast.success(`${validFiles.length} image(s) compressed and ready to upload`, {
          position: "top-center",
          autoClose: 2000
        });
      }
    } catch (error) {
      console.error('Error processing images:', error);
      toast.error('Failed to process images', { position: "top-center" });
    } finally {
      setIsCompressing(false);
      e.target.value = null;
    }
  };

  const removeNewImage = useCallback((index) => {
    const imageToRemove = newImages[index];
    if (imageToRemove instanceof File) {
      URL.revokeObjectURL(URL.createObjectURL(imageToRemove));
    }
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  }, [newImages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || isCompressing) return;
    
    if (newImages.length === 0) {
      toast.error('Please upload at least one product image', {
        position: "top-center",
        autoClose: 3000
      });
      return;
    }

    setIsLoading(true);

    const productPayload = {
      ...formData,
      currentNote: undefined,
      discountedPrices: (formData.offerType === 'Discount' || formData.offerType === 'Offer') ? formData.discountedPrices : undefined
    };

    const formDataToSend = new FormData();
    formDataToSend.append('product', JSON.stringify(productPayload));

    // Append compressed images
    newImages.forEach((file, index) => {
      formDataToSend.append(`image${index + 1}`, file);
    });

    // Log total payload size
    let totalSize = 0;
    for (let [key, value] of formDataToSend.entries()) {
      if (value instanceof File) {
        totalSize += value.size;
      }
    }
    console.log(`Total payload size: ${(totalSize / (1024 * 1024)).toFixed(2)}MB`);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/add-product`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          timeout: 60000 // 60 seconds timeout
        }
      );

      toast.success('Product added successfully!', {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });

      // Clear form after successful submission
      const initialPrices = {};
      const initialDiscountedPrices = {};
      perfumeVolumes.forEach(volume => {
        initialPrices[volume] = '';
        initialDiscountedPrices[volume] = '';
      });
      
      setFormData({
        name: '',
        prices: initialPrices,
        discountedPrices: initialDiscountedPrices,
        category: '',
        fragranceType: '',
        fragranceNotes: [],
        currentNote: '',
        stockAvailability: 'In stock',
        description: '',
        productType: 'Normal',
        offerType: 'None'
      });
      setOfferPercentage('');
      setNewImages([]);

      setTimeout(() => {
        onClose();
        if (onProductAdded && response.data) {
          onProductAdded(response.data);
        }
      }, 1500);

    } catch (error) {
      console.error('Error adding product:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to add product. Please try again.';
      
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      newImages.forEach(file => {
        if (file instanceof File) {
          URL.revokeObjectURL(URL.createObjectURL(file));
        }
      });
    };
  }, [newImages]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8 px-4">
      <ToastContainer />
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 p-4 sm:p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
              Add New Product
            </h2>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors" 
              disabled={isLoading || isCompressing}
              aria-label="Close form"
            >
              <FiX size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="overflow-y-auto flex-grow">
          <form id="add-product-form" onSubmit={handleSubmit} className="p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Product Name */}
              <div className="col-span-full">
                <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name *
                </label>
                <input 
                  id="productName" 
                  type="text" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base" 
                  placeholder="Enter product name" 
                  required 
                  disabled={isLoading || isCompressing} 
                />
              </div>

              {/* Fragrance Type */}
              <div className="col-span-full">
                <label htmlFor="fragranceType" className="block text-sm font-medium text-gray-700 mb-1">
                  Fragrance Type *
                </label>
                <select 
                  id="fragranceType" 
                  name="fragranceType" 
                  value={formData.fragranceType} 
                  onChange={handleChange} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base bg-white" 
                  required 
                  disabled={isLoading || isCompressing}
                >
                  <option value="">Select Fragrance Type</option>
                  {fragranceTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Product Type */}
              <div className="col-span-full md:col-span-1">
                <label htmlFor="productType" className="block text-sm font-medium text-gray-700 mb-1">
                  Product Type *
                </label>
                <select 
                  id="productType" 
                  name="productType" 
                  value={formData.productType} 
                  onChange={handleChange} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base bg-white" 
                  required 
                  disabled={isLoading || isCompressing}
                >
                  {productTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Offer Type */}
              <div className="col-span-full md:col-span-1">
                <label htmlFor="offerType" className="block text-sm font-medium text-gray-700 mb-1">
                  Offer Type *
                </label>
                <select 
                  id="offerType" 
                  name="offerType" 
                  value={formData.offerType} 
                  onChange={handleChange} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base bg-white" 
                  required 
                  disabled={isLoading || isCompressing}
                >
                  {offerTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Offer Percentage */}
              {showOfferPercentage && (
                <div className="col-span-full">
                  <label htmlFor="offerPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                    Offer Percentage *
                  </label>
                  <div className="relative max-w-xs">
                    <input 
                      id="offerPercentage" 
                      type="number" 
                      value={offerPercentage} 
                      onChange={handleOfferPercentageChange} 
                      min="0" 
                      max="100" 
                      step="0.01" 
                      className="w-full pr-8 pl-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base" 
                      placeholder="0.00" 
                      required={showOfferPercentage}
                      disabled={isLoading || isCompressing} 
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Discounted prices will be calculated automatically</p>
                </div>
              )}

              {/* Prices */}
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prices by Volume *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {volumes.map((volume) => (
                    <div key={volume} className="flex flex-col">
                      <label htmlFor={`price-${volume}`} className="text-xs sm:text-sm text-gray-600 mb-1">
                        {volume} *
                      </label>
                      <div className="relative">
                        <span className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">$</span>
                        <input 
                          id={`price-${volume}`} 
                          type="number" 
                          value={formData.prices[volume] || ''} 
                          onChange={(e) => handlePriceChange(volume, e.target.value)} 
                          min="0" 
                          step="0.01" 
                          className="w-full pl-6 sm:pl-8 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base" 
                          placeholder="0.00" 
                          required 
                          disabled={isLoading || isCompressing} 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Discounted Prices */}
              {showDiscountFields && (
                <div className="col-span-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.offerType === 'Offer' ? 'Offer Prices *' : 'Discounted Prices *'}
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {volumes.map((volume) => (
                      <div key={`discount-${volume}`} className="flex flex-col">
                        <label htmlFor={`discounted-price-${volume}`} className="text-xs sm:text-sm text-gray-600 mb-1">
                          {volume} *
                        </label>
                        <div className="relative">
                          <span className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">$</span>
                          <input 
                            id={`discounted-price-${volume}`} 
                            type="number" 
                            value={formData.discountedPrices[volume] || ''} 
                            onChange={(e) => handleDiscountedPriceChange(volume, e.target.value)} 
                            min="0" 
                            step="0.01" 
                            className="w-full pl-6 sm:pl-8 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base" 
                            placeholder="0.00" 
                            required={showDiscountFields}
                            disabled={isLoading || isCompressing || formData.offerType === 'Offer'} 
                            readOnly={formData.offerType === 'Offer'}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {formData.offerType === 'Offer' && (
                    <p className="text-xs text-gray-500 mt-1">These prices are automatically calculated based on the offer percentage</p>
                  )}
                </div>
              )}

              {/* Category & Stock */}
              <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select 
                    id="category" 
                    name="category" 
                    value={formData.category} 
                    onChange={handleChange} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base bg-white" 
                    required 
                    disabled={isLoading || isCompressing}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat, index) => (
                      <option key={index} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="stockAvailability" className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Availability *
                  </label>
                  <select 
                    id="stockAvailability" 
                    name="stockAvailability" 
                    value={formData.stockAvailability} 
                    onChange={handleChange} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base bg-white" 
                    required 
                    disabled={isLoading || isCompressing}
                  >
                    {stockOptions.map((status, index) => (
                      <option key={index} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Fragrance Notes */}
              <div className="col-span-full">
                <label htmlFor="fragranceNoteInput" className="block text-sm font-medium text-gray-700 mb-2">
                  Fragrance Notes
                </label>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <input 
                    id="fragranceNoteInput" 
                    type="text" 
                    value={formData.currentNote} 
                    onChange={(e) => setFormData((prev) => ({ ...prev, currentNote: e.target.value }))} 
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base" 
                    placeholder="Add a fragrance note (e.g., Citrus)" 
                    disabled={isLoading || isCompressing} 
                    onKeyDown={(e) => { 
                      if (e.key === 'Enter') { 
                        e.preventDefault(); 
                        handleAddNote(); 
                      } 
                    }} 
                  />
                  <button 
                    type="button" 
                    onClick={handleAddNote} 
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center justify-center gap-1 text-xs sm:text-sm text-white transition-colors disabled:bg-indigo-400 disabled:cursor-not-allowed" 
                    disabled={isLoading || isCompressing || !formData.currentNote.trim()}
                    aria-label="Add fragrance note"
                  >
                    <FiPlus size={16} /> Add
                  </button>
                </div>
                {formData.fragranceNotes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.fragranceNotes.map((note, index) => (
                      <div key={index} className="flex items-center bg-gray-100 px-3 py-1 rounded-full text-xs sm:text-sm hover:bg-gray-200 transition-colors">
                        <span>{note}</span>
                        <button 
                          type="button" 
                          onClick={() => handleRemoveNote(index)} 
                          className="ml-1.5 text-gray-500 hover:text-red-500 transition-colors disabled:text-gray-300" 
                          disabled={isLoading || isCompressing} 
                          aria-label={`Remove note ${note}`}
                        >
                          <FiTrash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Images Section */}
              <div className="col-span-full">
                <label htmlFor="productImages" className="block text-sm font-medium text-gray-700 mb-2">
                  Product Images ({totalImageCount} / {MAX_IMAGES}) {isCompressing && '- Compressing...'}
                </label>
                <div className="flex flex-col gap-3">
                  {/* File Input */}
                  {canUploadMore && (
                    <div>
                      <input
                        id="productImages" 
                        type="file" 
                        accept="image/png, image/jpeg, image/webp, image/jpg" 
                        multiple
                        onChange={handleImageChange}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isLoading || isCompressing || !canUploadMore}
                        aria-label="Upload product images"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Images will be automatically compressed. Max {MAX_FILE_SIZE_MB}MB per image.
                      </p>
                    </div>
                  )}
                  {!canUploadMore && (
                    <p className="text-xs text-gray-500">Maximum {MAX_IMAGES} images reached.</p>
                  )}

                  {isCompressing && (
                    <div className="flex items-center gap-2 text-sm text-indigo-600">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Compressing images...
                    </div>
                  )}

                  {/* Image Previews */}
                  {newImages.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-2">
                      {newImages.map((file, index) => (
                        file instanceof File && (
                          <div key={file.name + index} className="relative group">
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt={`Product preview ${index + 1}`} 
                              className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md border border-blue-300" 
                            />
                            <button
                              type="button"
                              onClick={() => removeNewImage(index)}
                              className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-75 group-hover:opacity-100 hover:bg-red-700 transition-all disabled:bg-red-300"
                              disabled={isLoading || isCompressing}
                              aria-label={`Remove image ${index + 1}`}
                            >
                              <FiX size={12} strokeWidth={3} />
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded-b-md text-center">
                              {(file.size / (1024 * 1024)).toFixed(2)}MB
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="col-span-full">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea 
                  id="description" 
                  name="description" 
                  value={formData.description} 
                  onChange={handleChange} 
                  rows="5" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base" 
                  placeholder="Enter detailed product description..." 
                  required 
                  disabled={isLoading || isCompressing} 
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white p-4 sm:p-6 border-t border-gray-200 mt-auto">
          <div className="flex justify-end space-x-3">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-4 py-2 sm:px-6 sm:py-3 border border-gray-300 rounded-lg shadow-sm text-sm sm:text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50" 
              disabled={isLoading || isCompressing}
              aria-label="Cancel and close form"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              form="add-product-form" 
              className="px-4 py-2 sm:px-6 sm:py-3 border border-transparent rounded-lg shadow-sm text-sm sm:text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 flex items-center justify-center min-w-[100px] sm:min-w-[120px]" 
              disabled={isLoading || isCompressing}
              aria-label="Add product"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding...
                </>
              ) : isCompressing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Compressing...
                </>
              ) : (
                'Add Product'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProductForm);